import { notification } from '@surecompanies/core_components'
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import epsClientV1 from 'enrollment-platform/api/eps-v1.client'
import {
  PaginationRequest,
  PaginationResponse,
} from 'enrollment-platform/types/pagination-response-dto'
import { downloadFile } from 'helpers/download-file'
import { useCallback, useEffect, useState } from 'react'

export interface BenchmarkSnapshotItem {
  id: number
  title: string
  description?: string
  isEnabled: boolean
  createdAt: string
  createdBy: string
}

export const DEFAULT_BENCHMARK_PAGE_SIZE = 50

async function fetchBenchmarkSnapshots(
  params: PaginationRequest
): Promise<PaginationResponse<BenchmarkSnapshotItem>> {
  const { data } = await epsClientV1.get<
    PaginationResponse<BenchmarkSnapshotItem>
  >('/benchmark-plans', { params })
  return data
}

async function downloadBenchmarkSnapshot(snapshotId: number): Promise<void> {
  const response = await epsClientV1.get(
    `/benchmark-plans/${snapshotId}/download`,
    { responseType: 'blob' }
  )
  downloadFile(response.data, `benchmark-plans-${snapshotId}.csv`)
}

async function deleteBenchmarkSnapshot(snapshotId: number): Promise<void> {
  await epsClientV1.delete(`/benchmark-plans/${snapshotId}`)
}

async function updateBenchmarkSnapshotEnabled(
  snapshotId: number,
  isEnabled: boolean
): Promise<void> {
  await epsClientV1.patch(`/benchmark-plans/${snapshotId}`, { isEnabled })
}

export function useBenchmarkSnapshots() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_BENCHMARK_PAGE_SIZE)

  const { data, isLoading, isPlaceholderData, error, refetch } = useQuery<
    PaginationResponse<BenchmarkSnapshotItem>,
    Error
  >({
    queryKey: ['benchmarkSnapshots', page, pageSize],
    queryFn: () =>
      fetchBenchmarkSnapshots({
        page,
        limit: pageSize,
      }),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    if (!error) return

    notification.error({
      message: 'Failed to load benchmark plans',
      description: error.message,
    })
  }, [error])

  const { mutate: onDownload, isPending: isDownloading } = useMutation({
    mutationFn: downloadBenchmarkSnapshot,
    onError: (err: Error) => {
      notification.error({
        message: 'Download failed',
        description: err.message,
      })
    },
  })

  const { mutate: onDelete, isPending: isDeleting } = useMutation({
    mutationFn: deleteBenchmarkSnapshot,
    onSuccess: () => {
      notification.success({ message: 'Snapshot deleted' })
      refetch()
    },
    onError: (err: Error) => {
      notification.error({
        message: 'Failed to delete snapshot',
        description: err.message,
      })
    },
  })

  const { mutate: onToggleEnabled, isPending: isTogglingEnabled } = useMutation(
    {
      mutationFn: ({ id, isEnabled }: { id: number; isEnabled: boolean }) =>
        updateBenchmarkSnapshotEnabled(id, isEnabled),
      onSuccess: () => refetch(),
      onError: (err: Error) => {
        notification.error({
          message: 'Failed to update snapshot status',
          description: err.message,
        })
      },
    }
  )

  const handlePageChange = useCallback(
    (nextPage: number, nextPageSize?: number) => {
      if (nextPageSize !== undefined && nextPageSize !== pageSize) {
        setPageSize(nextPageSize)
        setPage(1)
        return
      }

      setPage(nextPage)
    },
    [pageSize]
  )

  return {
    snapshots: data?.data ?? [],
    total: Number(data?.meta.total ?? 0),
    page,
    pageSize,
    isLoading: isLoading || isPlaceholderData,
    refetch,
    onDownload,
    isDownloading,
    onDelete,
    isDeleting,
    onToggleEnabled,
    isTogglingEnabled,
    handlePageChange,
  }
}
